// Vigilo Crowd Monitoring — design tokens, icons, and shared atoms.
// Converted from the Claude prototype (tokens.jsx) to typed TypeScript.
// Albert Sans — loaded via index.html. No Tailwind. Inline styles only.
import type { CSSProperties, ReactNode } from 'react';

// ─── Palette ─────────────────────────────────────────────────────────────────

export interface ColorPalette {
  bg:       string;
  sidebar:  string;
  surface:  string;
  panel:    string;
  panelHi:  string;
  border:   string;
  border2:  string;
  border3:  string;
  text:     string;
  sub:      string;
  muted:    string;
  faint:    string;
  amber:    string;
  amberDim: string;
  ember:    string;
  emberDim: string;
  blueGrey: string;
  blueGreyDim: string;
  teal:     string;
  tealDim:  string;
  green:    string;
  greenDim: string;
  yellow:   string;
  /** Time-of-day anomalies (Intel). Separate from `yellow` because `yellow` equals `amber` in the light theme. */
  gold:     string;
  // Operational status
  normal:   string;
  warning:  string;
  alert:    string;
  critical: string;
  // Forensics / Intel palette
  ceris:    string;
  cerisDim: string;
  purple:   string;
  purpleDim:string;
  noiBg:    string;
  noiBg2:   string;
}

export const VIGILO_DARK: ColorPalette = {
  bg:       '#0B1220',
  sidebar:  '#0F1A2E',
  surface:  '#111E33',
  panel:    '#16273F',
  panelHi:  '#1B2E4B',
  border:   'rgba(180,200,230,0.08)',
  border2:  'rgba(180,200,230,0.16)',
  border3:  'rgba(180,200,230,0.28)',
  text:     '#E8EEF7',
  sub:      'rgba(232,238,247,0.62)',
  muted:    'rgba(232,238,247,0.34)',
  faint:    'rgba(232,238,247,0.14)',
  amber:    '#D97706',
  amberDim: 'rgba(217,119,6,0.18)',
  ember:    '#C2410C',
  emberDim: 'rgba(194,65,12,0.20)',
  blueGrey: '#7890A8',
  blueGreyDim: 'rgba(120,144,168,0.16)',
  teal:     '#5BA3D0',
  tealDim:  'rgba(91,163,208,0.16)',
  green:    '#3F9D6E',
  greenDim: 'rgba(63,157,110,0.16)',
  yellow:   '#E8B86B',
  gold:     '#E8B86B',
  normal:   '#3F9D6E',
  warning:  '#D97706',
  alert:    '#C2410C',
  critical: '#E03E2D',
  ceris:    '#E0455E',
  cerisDim: 'rgba(224,69,94,0.18)',
  purple:   '#9B7FE0',
  purpleDim:'rgba(155,127,224,0.18)',
  noiBg:    '#1A0A4A',
  noiBg2:   '#1F0E52',
};

export const VIGILO_LIGHT: ColorPalette = {
  bg:       '#F4F1FA',
  sidebar:  '#1A0650',
  surface:  '#FFFFFF',
  panel:    '#FFFFFF',
  panelHi:  '#F3EDFF',
  border:   'rgba(51,12,137,0.08)',
  border2:  'rgba(51,12,137,0.14)',
  border3:  'rgba(51,12,137,0.22)',
  text:     '#1A0650',
  sub:      'rgba(26,6,80,0.66)',
  muted:    'rgba(26,6,80,0.40)',
  faint:    'rgba(26,6,80,0.10)',
  amber:    '#B45309',
  amberDim: 'rgba(180,83,9,0.14)',
  ember:    '#9A2D0B',
  emberDim: 'rgba(154,45,11,0.14)',
  blueGrey: '#607486',
  blueGreyDim: 'rgba(96,116,134,0.12)',
  teal:     '#2A6E94',
  tealDim:  'rgba(42,110,148,0.12)',
  green:    '#2E7D52',
  greenDim: 'rgba(46,125,82,0.14)',
  yellow:   '#B45309',
  gold:     '#7A6C00',
  normal:   '#2E7D52',
  warning:  '#B45309',
  alert:    '#9A2D0B',
  critical: '#B91C1C',
  ceris:    '#C1304A',
  cerisDim: 'rgba(193,48,74,0.12)',
  purple:   '#6D4DBF',
  purpleDim:'rgba(109,77,191,0.12)',
  noiBg:    '#1A0A4A',
  noiBg2:   '#241261',
};

export function getC(theme: 'dark' | 'light'): ColorPalette {
  return theme === 'light' ? VIGILO_LIGHT : VIGILO_DARK;
}

// ─── Icons ────────────────────────────────────────────────────────────────────

export interface IconProps {
  size?: number;
  sw?:   number;
}

interface IcoProps extends IconProps {
  d: string | string[];
}

const Ico = ({ d, size = 16, sw = 1.5 }: IcoProps) => (
  <svg
    width={size} height={size} viewBox="0 0 24 24"
    fill="none" stroke="currentColor"
    strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round"
    style={{ flexShrink: 0 }}
  >
    {Array.isArray(d)
      ? d.map((p, i) => <path key={i} d={p} />)
      : <path d={d} />}
  </svg>
);

export type IconFn = (props: IconProps) => JSX.Element;

export const Icons: Record<string, IconFn> = {
  Map:      (p) => <Ico {...p} d={['M3 6l6-3 6 3 6-3v15l-6 3-6-3-6 3z','M9 3v15','M15 6v15']} />,
  Zones:    (p) => <Ico {...p} d={['M3 9l9-6 9 6-9 6z','M3 14l9 6 9-6','M3 9v5','M21 9v5']} />,
  Alert:    (p) => <Ico {...p} d={['M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z','M12 9v4','M12 17h.01']} />,
  Grid:     (p) => <Ico {...p} d={['M3 3h7v7H3z','M14 3h7v7h-7z','M3 14h7v7H3z','M14 14h7v7h-7z']} />,
  Activity: (p) => <Ico {...p} d={['M22 12h-4l-3 9L9 3l-3 9H2']} />,
  Report:   (p) => <Ico {...p} d={['M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z','M14 2v6h6','M16 13H8','M16 17H8']} />,
  Settings: (p) => <Ico {...p} d={['M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z','M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33h0a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82v0a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z']} />,
  Search:   (p) => <Ico {...p} d={['M11 17.25a6.25 6.25 0 1 1 0-12.5 6.25 6.25 0 0 1 0 12.5z','M16 16l4.5 4.5']} />,
  Pin:      (p) => <Ico {...p} d={['M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z','M12 10a3 3 0 1 0 0-6 3 3 0 0 0 0 6z']} />,
  Clock:    (p) => <Ico {...p} d={['M12 2a10 10 0 1 0 0 20A10 10 0 0 0 12 2z','M12 6v6l4 2']} />,
  Bell:     (p) => <Ico {...p} d={['M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9','M13.73 21a2 2 0 0 1-3.46 0']} />,
  Users:    (p) => <Ico {...p} d={['M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2','M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z','M23 21v-2a4 4 0 0 0-3-3.87','M16 3.13a4 4 0 0 1 0 7.75']} />,
  Flag:     (p) => <Ico {...p} d={['M4 21V4','M4 4h14l-3 4 3 4H4']} />,
  Play:     (p) => <Ico {...p} d={['M5 3v18l16-9z']} />,
  Pause:    (p) => <Ico {...p} d={['M6 4h4v16H6z','M14 4h4v16h-4z']} />,
  Layers:   (p) => <Ico {...p} d={['M12 2L2 7l10 5 10-5z','M2 17l10 5 10-5','M2 12l10 5 10-5']} />,
  List:     (p) => <Ico {...p} d={['M8 6h13','M8 12h13','M8 18h13','M3 6h.01','M3 12h.01','M3 18h.01']} />,
  Plus:     (p) => <Ico {...p} d={['M12 5v14','M5 12h14']} />,
  ChevR:    (p) => <Ico {...p} d={['M9 18l6-6-6-6']} />,
  Check:    (p) => <Ico {...p} d={['M20 6L9 17l-5-5']} />,
  X:        (p) => <Ico {...p} d={['M18 6L6 18','M6 6l12 12']} />,
  ArrowR:   (p) => <Ico {...p} d={['M5 12h14','M12 5l7 7-7 7']} />,
  Eye:      (p) => <Ico {...p} d={['M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z','M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z']} />,
  Download: (p) => <Ico {...p} d={['M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4','M7 10l5 5 5-5','M12 15V3']} />,
  Export:   (p) => <Ico {...p} d={['M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4','M17 8l-5-5-5 5','M12 3v12']} />,
  Calendar: (p) => <Ico {...p} d={['M19 4H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2z','M16 2v4','M8 2v4','M3 10h18']} />,
  Zap:      (p) => <Ico {...p} d={['M13 2L3 14h9l-1 8 10-12h-9z']} />,
  Shield:   (p) => <Ico {...p} d={['M12 2l9 4v6c0 5-3.5 9.5-9 10-5.5-.5-9-5-9-10V6z']} />,
  Edit:     (p) => <Ico {...p} d={['M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7','M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4z']} />,
  Trash:    (p) => <Ico {...p} d={['M3 6h18','M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6','M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2']} />,
  Refresh:  (p) => <Ico {...p} d={['M23 4v6h-6','M1 20v-6h6','M3.51 9a9 9 0 0 1 14.85-3.36L23 10','M1 14l4.64 4.36A9 9 0 0 0 20.49 15']} />,
  Filter:   (p) => <Ico {...p} d={['M22 3H2l8 9.46V19l4 2v-8.54z']} />,
  Info:     (p) => <Ico {...p} d={['M12 2a10 10 0 1 0 0 20A10 10 0 0 0 12 2z','M12 16v-4','M12 8h.01']} />,
  More:     (p) => <Ico {...p} d={['M12 13a1 1 0 1 0 0-2 1 1 0 0 0 0 2z','M19 13a1 1 0 1 0 0-2 1 1 0 0 0 0 2z','M5 13a1 1 0 1 0 0-2 1 1 0 0 0 0 2z']} />,
  Lock:     (p) => <Ico {...p} d={['M5 11h14v10H5z','M8 11V7a4 4 0 1 1 8 0v4']} />,
  Save:     (p) => <Ico {...p} d={['M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z','M17 21v-8H7v8','M7 3v5h8']} />,
  ChevL:    (p) => <Ico {...p} d={['M15 18l-6-6 6-6']} />,
  ChevD:    (p) => <Ico {...p} d={['M6 9l6 6 6-6']} />,
  Up:       (p) => <Ico {...p} d={['M7 11l5-5 5 5','M7 18l5-5 5 5']} />,
  Down:     (p) => <Ico {...p} d={['M7 13l5 5 5-5','M7 6l5 5 5-5']} />,
  ArrowU:   (p) => <Ico {...p} d={['M12 19V5','M5 12l7-7 7 7']} />,
  ArrowD:   (p) => <Ico {...p} d={['M12 5v14','M19 12l-7 7-7-7']} />,
  Triangle: (p) => <Ico {...p} d={['M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z']} />,
  Square:   (p) => <Ico {...p} d={['M3 3h18v18H3z']} />,
  Circle:   (p) => <Ico {...p} d={['M12 2a10 10 0 1 0 0 20A10 10 0 0 0 12 2z']} />,
  Flow:     (p) => <Ico {...p} d={['M3 6h13','M16 6l-3-3','M16 6l-3 3','M21 12H8','M8 12l3-3','M8 12l3 3','M3 18h13','M16 18l-3-3','M16 18l-3 3']} />,
  Crosshair:(p) => <Ico {...p} d={['M12 2a10 10 0 1 0 0 20A10 10 0 0 0 12 2z','M22 12h-4','M6 12H2','M12 6V2','M12 22v-4']} />,
  Globe:    (p) => <Ico {...p} d={['M12 2a10 10 0 1 0 0 20A10 10 0 0 0 12 2z','M2 12h20','M12 2a15 15 0 0 1 0 20','M12 2a15 15 0 0 0 0 20']} />,
  EyeOff:  (p) => <Ico {...p} d={['M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24','M1 1l22 22']} />,
  Heart:    (p) => <Ico {...p} d={['M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 1 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z']} />,
  Archive:  (p) => <Ico {...p} d={['M20 8H4v12a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2z', 'M22 3H2v5h20z', 'M10 13h4']} />,
  // Border / maritime specific
  Boat:       (p) => <Ico {...p} d={['M3 18c1.5 1 3 1 4.5 0s3-1 4.5 0 3 1 4.5 0 3-1 4.5 0','M5 14l1.5-7h11L19 14','M12 7V3','M9 3h6']} />,
  Waves:      (p) => <Ico {...p} d={['M2 8c2-2 4-2 6 0s4 2 6 0 4-2 6 0','M2 13c2-2 4-2 6 0s4 2 6 0 4-2 6 0','M2 18c2-2 4-2 6 0s4 2 6 0 4-2 6 0']} />,
  Anchor:     (p) => <Ico {...p} d={['M12 2v18','M9 5a3 3 0 1 1 6 0 3 3 0 0 1-6 0z','M5 12H3a9 9 0 0 0 18 0h-2','M8 11H4','M20 11h-4']} />,
  Compass:    (p) => <Ico {...p} d={['M12 2a10 10 0 1 0 0 20A10 10 0 0 0 12 2z','M16.24 7.76l-2.12 6.36-6.36 2.12 2.12-6.36z']} />,
  Crossing:   (p) => <Ico {...p} d={['M3 12h18','M16 7l5 5-5 5','M8 5l-5 7 5 7']} />,
  Fingerprint:(p) => <Ico {...p} d={['M12 11v3','M6 7a8 8 0 0 1 12 0','M4 11a10 10 0 0 1 16 0v3a8 8 0 0 1-.5 2.8','M8 13a4 4 0 0 1 8 0v2a6 6 0 0 1-1 3.3','M12 11a2 2 0 0 1 2 2v3']} />,
  Path:       (p) => <Ico {...p} d={['M5 18C5 18 6 12 10 12C14 12 14 6 18 6','M5 18a2 2 0 1 1 0-0.01z','M18 6a2 2 0 1 1 0-0.01z']} />,
  Briefcase:  (p) => <Ico {...p} d={['M3 8h18v12a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8z','M8 8V6a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2','M3 13h18']} />,
  Send:       (p) => <Ico {...p} d={['M22 2L11 13','M22 2l-7 20-4-9-9-4z']} />,
  Phone:      (p) => <Ico {...p} d={['M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z']} />,
  PhoneOff:   (p) => <Ico {...p} d={['M10.68 13.31a16 16 0 0 0 3.41 2.6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.69 19.69 0 0 1-1.72-1.31','M1 1l22 22','M6.6 6.6A19.27 19.27 0 0 0 2.82 9.33 2 2 0 0 0 2 11.07v3a2 2 0 0 0 1.72 1.97 19.79 19.79 0 0 0 2.81.7 2 2 0 0 0 2.11-.45l1.27-1.27a19.5 19.5 0 0 0 5.22 3.28']} />,
  Power:      (p) => <Ico {...p} d={['M12 2v10','M5.45 7A8 8 0 1 0 18.55 7']} />,
  MapPin:     (p) => <Ico {...p} d={['M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0z','M12 13a3 3 0 1 0 0-6 3 3 0 0 0 0 6z']} />,
  PowerOff:   (p) => <Ico {...p} d={['M12 2v10','M5.45 7A8 8 0 1 0 18.55 7','M3 3l18 18']} />,
  SkipBack:   (p) => <Ico {...p} d={['M19 20L9 12l10-8z','M5 4v16']} />,
  SkipForward:(p) => <Ico {...p} d={['M5 4l10 8-10 8z','M19 4v16']} />,
  BarChart:   (p) => <Ico {...p} d={['M3 3v18h18','M8 17v-4','M13 17V8','M18 17v-7']} />,
  Mobile:     (p) => <Ico {...p} d={['M7 2h10a2 2 0 0 1 2 2v16a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2z','M11 18h2']} />,
  Sim:        (p) => <Ico {...p} d={['M9 2h6a2 2 0 0 1 2 2v16a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2z','M11 18h2','M7 8h10','M7 13h10']} />,
  // Critical Infrastructure specific
  Building:   (p) => <Ico {...p} d={['M3 21h18','M5 21V5a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v16','M15 21V9a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v12','M9 7h2','M9 11h2','M9 15h2']} />,
  Atom:       (p) => <Ico {...p} d={['M12 12a1 1 0 1 0 0-2 1 1 0 0 0 0 2z','M12 2a10 10 0 1 0 0 20A10 10 0 0 0 12 2z','M4.5 9.5c5-3 10-3 15 0','M4.5 14.5c5 3 10 3 15 0','M9.5 4.5c-3 5-3 10 0 15','M14.5 4.5c3 5 3 10 0 15']} />,
  Drop:       (p) => <Ico {...p} d={['M12 2.5C12 2.5 5 10 5 14.5a7 7 0 0 0 14 0C19 10 12 2.5 12 2.5z']} />,
  Train:      (p) => <Ico {...p} d={['M6 3h12a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2z','M4 11h16','M8 16l-2 4','M16 16l2 4','M9 7h6']} />,
  Landmark:   (p) => <Ico {...p} d={['M3 21h18','M3 10h18','M12 3l9 5H3z','M6 10v8','M10 10v8','M14 10v8','M18 10v8']} />,
  Radio:      (p) => <Ico {...p} d={['M12 12a1 1 0 1 0 0-2 1 1 0 0 0 0 2z','M7.5 7.5a6 6 0 0 0 0 9','M16.5 7.5a6 6 0 0 1 0 9','M4.5 4.5a10 10 0 0 0 0 15','M19.5 4.5a10 10 0 0 1 0 15']} />,
  Key:        (p) => <Ico {...p} d={['M15.5 8.5a3.5 3.5 0 1 1-4.9 4.1L4 19.2V21h1.8l1-1h2v-2h2l1.6-1.6a3.5 3.5 0 0 0 3.1-7.9z','M16.5 7.5h.01']} />,
  Wrench:     (p) => <Ico {...p} d={['M14.7 6.3a4 4 0 0 0-5.2 5.2L3 18l3 3 6.5-6.5a4 4 0 0 0 5.2-5.2l-2.3 2.3-2.7-.7-.7-2.7z']} />,
  Clipboard:  (p) => <Ico {...p} d={['M9 3h6a1 1 0 0 1 1 1v1a1 1 0 0 1-1 1H9a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1z','M8 4H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2h-2','M9 12h6','M9 16h4']} />,
  Target:     (p) => <Ico {...p} d={['M12 2a10 10 0 1 0 0 20A10 10 0 0 0 12 2z','M12 7a5 5 0 1 0 0 10A5 5 0 0 0 12 7z','M12 11a1 1 0 1 0 0 2 1 1 0 0 0 0-2z']} />,
  Server:     (p) => <Ico {...p} d={['M4 4h16a1 1 0 0 1 1 1v4a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1z','M4 14h16a1 1 0 0 1 1 1v4a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1v-4a1 1 0 0 1 1-1z','M7 7h.01','M7 17h.01']} />,
  UserX:      (p) => <Ico {...p} d={['M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2','M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z','M17 8l5 5','M22 8l-5 5']} />,
  Hourglass:  (p) => <Ico {...p} d={['M6 2h12','M6 22h12','M6 2c0 4 3 6 6 6s6-2 6-6','M6 22c0-4 3-6 6-6s6 2 6 6']} />,
  Bolt:       (p) => <Ico {...p} d={['M13 2L3 14h9l-1 8 10-12h-9z']} />,
  // Forensics-specific icons
  Call:     (p) => <Ico {...p} d={"M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z"} />,
  Sms:      (p) => <Ico {...p} d={"M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8z"} />,
  Signal:   (p) => <Ico {...p} d={["M2 20h.01","M7 20v-4","M12 20v-8","M17 20V8","M22 4v16"]} />,
  Handset:  (p) => <Ico {...p} d={["M5 4h14a1 1 0 0 1 1 1v14a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1z","M9 8h6","M12 16h.01"]} />,
  Roaming:  (p) => <Ico {...p} d={["M12 2a10 10 0 1 0 0 20A10 10 0 0 0 12 2z","M2 12h20","M12 2a15 15 0 0 1 0 20","M12 2a15 15 0 0 0 0 20","M16 5l3-1-1 3"]} />,
  Hash:     (p) => <Ico {...p} d={["M4 9h16","M4 15h16","M10 3L8 21","M16 3l-2 18"]} />,
  Package:  (p) => <Ico {...p} d={["M12 2l9 5v10l-9 5-9-5V7z","M3.27 7L12 12l8.73-5","M12 22V12"]} />,
  Scale:    (p) => <Ico {...p} d={["M12 3v18","M5 21h14","M12 6l-7 2 3 6a3 3 0 0 1-6 0l3-6","M12 6l7 2-3 6a3 3 0 0 0 6 0l-3-6"]} />,
  Maximize: (p) => <Ico {...p} d={["M15 3h6v6","M9 21H3v-6","M21 3l-7 7","M3 21l7-7"]} />,
  Tower:    (p) => <Ico {...p} d={["M12 8a2 2 0 1 0 0-4 2 2 0 0 0 0 4z","M12 8v13","M8 21h8","M6.34 4.34a8 8 0 0 0 0 11.32","M17.66 4.34a8 8 0 0 1 0 11.32","M9.17 7.17a4 4 0 0 0 0 5.66","M14.83 7.17a4 4 0 0 1 0 5.66"]} />,
  Warrant:  (p) => <Ico {...p} d={["M9 12l2 2 4-4","M12 3l8 4v5c0 5-3.5 8.5-8 9-4.5-.5-8-4-8-9V7z"]} />,
  Grid2:    (p) => <Ico {...p} d={["M3 3h7v7H3z","M14 3h7v7h-7z","M3 14h7v7H3z","M14 14h7v7h-7z"]} />,
  Network:  (p) => <Ico {...p} d={["M5 6a2 2 0 1 0 0-4 2 2 0 0 0 0 4z","M19 6a2 2 0 1 0 0-4 2 2 0 0 0 0 4z","M12 22a2 2 0 1 0 0-4 2 2 0 0 0 0 4z","M6.5 5.5L11 18","M17.5 5.5L13 18","M6 4h12"]} />,
  Doc:      (p) => <Ico {...p} d={["M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z","M14 2v6h6","M16 13H8","M16 17H8","M10 9H8"]} />,
  Folder:   (p) => <Ico {...p} d={["M3 7a2 2 0 0 1 2-2h4l2 3h8a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"]} />,
  Person:   (p) => <Ico {...p} d={["M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2","M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z"]} />,
  Car:      (p) => <Ico {...p} d={["M5 17a2 2 0 1 0 0-.01z","M19 17a2 2 0 1 0 0-.01z","M3 13l2-6a2 2 0 0 1 2-1.4h10A2 2 0 0 1 19 7l2 6","M3 13h18v4H3z"]} />,
  Link:     (p) => <Ico {...p} d={["M10 13a5 5 0 0 0 7 0l3-3a5 5 0 0 0-7-7l-1.5 1.5","M14 11a5 5 0 0 0-7 0l-3 3a5 5 0 0 0 7 7l1.5-1.5"]} />,
  Note:     (p) => <Ico {...p} d={["M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7","M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4z"]} />,
  Pulse:    (p) => <Ico {...p} d={"M22 12h-4l-3 9L9 3l-3 9H2"} />,
  Brain:    (p) => <Ico {...p} d={["M9.5 2A2.5 2.5 0 0 1 12 4.5v15a2.5 2.5 0 0 1-4.96.44 2.5 2.5 0 0 1-2.96-3.08 3 3 0 0 1-.34-5.58 2.5 2.5 0 0 1 1.32-4.24A2.5 2.5 0 0 1 9.5 2z","M14.5 2A2.5 2.5 0 0 0 12 4.5v15a2.5 2.5 0 0 0 4.96.44 2.5 2.5 0 0 0 2.96-3.08 3 3 0 0 0 .34-5.58 2.5 2.5 0 0 0-1.32-4.24A2.5 2.5 0 0 0 14.5 2z"]} />,
  ZoomIn:   (p) => <Ico {...p} d={["M11 17.25a6.25 6.25 0 1 1 0-12.5 6.25 6.25 0 0 1 0 12.5z","M16 16l4.5 4.5","M11 8v6","M8 11h6"]} />,
  ZoomOut:  (p) => <Ico {...p} d={["M11 17.25a6.25 6.25 0 1 1 0-12.5 6.25 6.25 0 0 1 0 12.5z","M16 16l4.5 4.5","M8 11h6"]} />,
  ZoneEnter:(p) => <Ico {...p} d={['M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z','M9 9l3-3 3 3','M12 6v7']} />,
  ZoneExit: (p) => <Ico {...p} d={['M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z','M9 10l3 3 3-3','M12 13V6']} />,
  // Intel timeline and pattern of life: anomaly types and group shapes.
  Moon:     (p) => <Ico {...p} d={['M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z']} />,
  Repeat:   (p) => <Ico {...p} d={['M17 1l4 4-4 4','M3 11V9a4 4 0 0 1 4-4h14','M7 23l-4-4 4-4','M21 13v2a4 4 0 0 1-4 4H3']} />,
  PinOff:   (p) => <Ico {...p} d={['M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z','M12 10a3 3 0 1 0 0-6 3 3 0 0 0 0 6z','M3 3l18 18']} />,
  Gauge:    (p) => <Ico {...p} d={['M12 14l4-4','M3.34 19a10 10 0 1 1 17.32 0']} />,
  Loop:     (p) => <Ico {...p} d={['M12 12a1 1 0 0 1 2 0 3 3 0 0 1-6 0 5 5 0 0 1 10 0 7 7 0 0 1-14 0']} />,
  Diamond:  (p) => <Ico {...p} d={['M12 2l10 10-10 10L2 12z']} />,
  Hexagon:  (p) => <Ico {...p} d={['M12 2l9 5v10l-9 5-9-5V7z']} />,
};

// ─── Typography scale hooks ───────────────────────────────────────────────────
// Shared atoms size their text as calc(Npx * var(--vt-*, 1)). Without the variables nothing changes;
// an app that wants larger, more readable text sets them once on its root element (see the Forensics theme).
//   --vt-label : small uppercase section labels   --vt-badge : pills and badges
//   --vt-ctl   : buttons, tabs and form controls   --vt-body  : hints and helper text
export const vt = (px: number, token: 'label' | 'badge' | 'ctl' | 'body'): string => `calc(${px}px * var(--vt-${token}, 1))`;

// ─── Shared atoms ─────────────────────────────────────────────────────────────

export interface KLabelProps { children: ReactNode; C: ColorPalette; mb?: number; tone?: 'muted' | 'sub'; }
export const KLabel = ({ children, C, mb = 6, tone = 'muted' }: KLabelProps) => (
  <div style={{
    fontSize: vt(9, 'label'), fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase',
    color: tone === 'sub' ? C.sub : C.muted, marginBottom: mb,
  }}>{children}</div>
);

export interface StatusDotProps { color: string; pulse?: boolean; size?: number; }
export const StatusDot = ({ color, pulse, size = 6 }: StatusDotProps) => (
  <span style={{
    width: size, height: size, background: color, borderRadius: '50%',
    boxShadow: pulse ? `0 0 0 ${size / 2}px ${color}30` : 'none',
    flexShrink: 0, display: 'inline-block',
  }} />
);

export interface BadgeProps {
  children: ReactNode;
  color?: string;
  bg?: string;
  border?: string;
  C: ColorPalette;
  size?: number;
}
export const Badge = ({ children, color, bg, border, C, size = 10 }: BadgeProps) => (
  <span style={{
    display: 'inline-flex', alignItems: 'center', gap: 4,
    fontSize: vt(size, 'badge'), fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase',
    color: color ?? C.text, background: bg ?? C.faint,
    border: border ? `1px solid ${border}` : 'none',
    padding: '2px 6px', borderRadius: 3, lineHeight: 1.3,
  }}>{children}</span>
);

export interface PillProps { children: ReactNode; color: string; hollow?: boolean; }
export const Pill = ({ children, color, hollow }: PillProps) => (
  <span style={{
    display: 'inline-flex', alignItems: 'center', gap: 4,
    fontSize: vt(10, 'badge'), fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase',
    color: hollow ? color : '#fff',
    background: hollow ? `${color}22` : color,
    border: hollow ? `1px solid ${color}55` : 'none',
    padding: '2px 7px', borderRadius: 9999, lineHeight: 1.4,
  }}>{children}</span>
);

export interface PanelProps { children: ReactNode; C: ColorPalette; p?: number; style?: CSSProperties; onClick?: () => void; }
export const Panel = ({ children, C, p = 0, style, onClick }: PanelProps) => (
  <div onClick={onClick} style={{ background: C.panel, border: `1px solid ${C.border2}`, borderRadius: 8, padding: p, ...style }}>
    {children}
  </div>
);

export interface BtnProps {
  children?: ReactNode;
  variant?: 'primary' | 'ghost' | 'outline' | 'danger' | 'success';
  C: ColorPalette;
  onClick?: () => void;
  style?: CSSProperties;
  size?: 'sm' | 'md' | 'lg';
  icon?: ReactNode;
  disabled?: boolean;
}
export const Btn = ({ children, variant = 'ghost', C, onClick, style, size = 'md', icon, disabled }: BtnProps) => {
  const sizes = {
    sm: { fontSize: vt(11, 'ctl'), padding: '5px 10px', minHeight: 26 },
    md: { fontSize: vt(12, 'ctl'), padding: '7px 12px', minHeight: 32 },
    lg: { fontSize: vt(13, 'ctl'), padding: '9px 16px', minHeight: 38 },
  };
  const variants = {
    primary: { background: C.amber,    color: '#fff',   border: `1px solid ${C.amber}` },
    ghost:   { background: 'transparent', color: C.sub, border: `1px solid ${C.border2}` },
    outline: { background: 'transparent', color: C.text, border: `1px solid ${C.border3}` },
    danger:  { background: C.emberDim, color: C.ember,  border: `1px solid ${C.ember}50` },
    success: { background: C.greenDim, color: C.green,  border: `1px solid ${C.green}50` },
  };
  return (
    <button
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 6,
        fontFamily: "'Albert Sans', sans-serif", fontWeight: 600, letterSpacing: '0.02em',
        borderRadius: 5, cursor: disabled ? 'not-allowed' : 'pointer',
        transition: 'all 120ms ease', opacity: disabled ? 0.5 : 1, whiteSpace: 'nowrap',
        ...sizes[size], ...variants[variant], ...style,
      }}
    >
      {icon}{children}
    </button>
  );
};

export interface TabItem { id: string; label: string; count?: number; }
export interface TabsProps { tabs: TabItem[]; active: string; onChange: (id: string) => void; C: ColorPalette; }
export const Tabs = ({ tabs, active, onChange, C }: TabsProps) => (
  <div style={{ display: 'flex', gap: 0, borderBottom: `1px solid ${C.border2}` }}>
    {tabs.map(t => {
      const isActive = active === t.id;
      return (
        <button key={t.id} onClick={() => onChange(t.id)} style={{
          padding: '10px 16px', fontSize: vt(12, 'ctl'), fontWeight: 600,
          fontFamily: "'Albert Sans', sans-serif",
          color: isActive ? C.text : C.sub, background: 'transparent',
          border: 'none', borderBottom: `2px solid ${isActive ? C.amber : 'transparent'}`,
          cursor: 'pointer', marginBottom: -1, transition: 'all 120ms',
        }}>
          {t.label}
          {t.count != null && (
            <span style={{
              marginLeft: 6, fontSize: vt(10, 'badge'), padding: '1px 5px', borderRadius: 9,
              background: isActive ? C.amberDim : C.faint,
              color: isActive ? C.amber : C.muted,
            }}>{t.count}</span>
          )}
        </button>
      );
    })}
  </div>
);

// ─── Color helpers ────────────────────────────────────────────────────────────

export function SeverityColor(C: ColorPalette, sev: string): string {
  const map: Record<string, string> = {
    critical: C.critical, alert: C.alert, high: C.ember,
    warning: C.amber, medium: C.amber, low: C.teal, info: C.teal, informational: C.teal, normal: C.green,
  };
  return map[sev] ?? C.muted;
}

export function StatusColor(C: ColorPalette, status: string): string {
  const map: Record<string, string> = {
    normal: C.green, ok: C.green, active: C.green,
    warning: C.amber, watch: C.amber,
    alert: C.ember, critical: C.critical, urgent: C.critical,
    inactive: C.muted, archived: C.muted, closed: C.muted, resolved: C.muted,
    acknowledged: C.teal, escalated: C.ember,
  };
  return map[status] ?? C.muted;
}

// ─── Forensics colour helpers ─────────────────────────────────────────────────

export function ClassColor(C: ColorPalette, cls: string): string {
  const map: Record<string, string> = {
    HVT: C.ceris, hvt: C.ceris, POI: C.amber, poi: C.amber,
    Associate: C.purple, associate: C.purple, Unknown: C.muted, unknown: C.muted,
  };
  return map[cls] ?? C.muted;
}

export function ThreatColor(C: ColorPalette, t: string): string {
  const map: Record<string, string> = {
    critical: C.critical, high: C.ember, medium: C.amber, low: C.teal,
  };
  return map[(t || '').toLowerCase()] ?? C.muted;
}

export function LevelColor(C: ColorPalette, lvl: string): string {
  const map: Record<string, string> = { Restricted: C.teal, Confidential: C.amber, Secret: C.ceris };
  return map[lvl] ?? C.muted;
}

export function CaseStatusColor(C: ColorPalette, s: string): string {
  const map: Record<string, string> = {
    open: C.muted, active: C.purple, pending_warrant: C.amber,
    suspended: C.blueGrey, pending_closure: C.ember, closed: C.green,
  };
  return map[s] ?? C.muted;
}

export const CASE_STATUS_LABEL: Record<string, string> = {
  open: 'Open', active: 'Active', pending_warrant: 'Pending Warrant',
  suspended: 'Suspended', pending_closure: 'Pending Closure', closed: 'Closed',
};

export function CASE_TYPE_COLOR(C: ColorPalette, t: string): string {
  const map: Record<string, string> = {
    Crime: C.ceris, Terrorism: C.critical, Disappearance: C.teal,
    'Organised Crime': C.amber, Other: C.muted,
  };
  return map[t] ?? C.muted;
}

/**
 * Burner score bands, shared by every colour, legend, filter and label so they
 * cannot disagree: `warning` and above is elevated (amber), `critical` and above is
 * high (red), anything else is low (grey). An app can override the defaults once
 * at start-up with configureBurnerThresholds.
 */
export const BURNER_THRESHOLDS = { warning: 50, critical: 75 };

export function configureBurnerThresholds(next: { warning?: number; critical?: number }): void {
  if (Number.isFinite(next.warning)) BURNER_THRESHOLDS.warning = next.warning as number;
  if (Number.isFinite(next.critical)) BURNER_THRESHOLDS.critical = next.critical as number;
}

export function BurnerBand(score: number): 'high' | 'elevated' | 'low' {
  return score >= BURNER_THRESHOLDS.critical ? 'high' : score >= BURNER_THRESHOLDS.warning ? 'elevated' : 'low';
}

export function BurnerColor(C: ColorPalette, score: number): string {
  const band = BurnerBand(score);
  return band === 'high' ? C.ceris : band === 'elevated' ? C.amber : C.muted;
}

export interface MaskedProps { C: ColorPalette; value?: string; revealed?: boolean; mono?: boolean; }
export const Masked = ({ C, value, revealed, mono = true }: MaskedProps) => (
  revealed
    ? <span style={{ fontFamily: mono ? 'monospace' : undefined, color: C.text, fontWeight: 600 }}>{value}</span>
    : <span style={{ fontStyle: 'italic', color: C.muted, fontWeight: 500, letterSpacing: '0.04em' }}>MASKED</span>
);

export function WarrantBannerColor(C: ColorPalette, state: string): string {
  const map: Record<string, string> = {
    active: C.green, pending: C.amber, expired: C.critical, none: C.muted,
  };
  return map[state] ?? C.muted;
}

export function inputStyle(C: ColorPalette): CSSProperties {
  return {
    width: '100%', padding: '9px 11px', fontSize: vt(12.5, 'ctl'), background: C.surface, color: C.text,
    border: `1px solid ${C.border3}`, borderRadius: 5, fontFamily: "'Albert Sans', sans-serif", outline: 'none',
  };
}

export interface FieldProps { C: ColorPalette; label: string; hint?: string; children: ReactNode; }
export const Field = ({ C, label, hint, children }: FieldProps) => (
  <div style={{ marginBottom: 16 }}>
    <div style={{ fontSize: vt(10.5, 'body'), fontWeight: 600, color: C.sub, marginBottom: 6 }}>{label}</div>
    {children}
    {hint && <div style={{ fontSize: vt(9.5, 'body'), color: C.muted, marginTop: 5, lineHeight: 1.5 }}>{hint}</div>}
  </div>
);

export interface SegRowOption { value: string; label: string; }
export interface SegRowProps { C: ColorPalette; options: (string | SegRowOption)[]; value: string; onChange: (v: string) => void; cap?: boolean; }
export const SegRow = ({ C, options, value, onChange, cap = true }: SegRowProps) => (
  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
    {options.map(o => {
      const val = typeof o === 'object' ? o.value : o;
      const lab = typeof o === 'object' ? o.label : o;
      return (
        <button key={val} onClick={() => onChange(val)} style={{
          padding: '7px 14px', fontSize: vt(11.5, 'ctl'), fontWeight: 600, borderRadius: 5, cursor: 'pointer',
          fontFamily: "'Albert Sans', sans-serif", textTransform: cap ? 'capitalize' : 'none',
          background: value === val ? C.amberDim : 'transparent',
          border: `1px solid ${value === val ? C.amber + '66' : C.border2}`,
          color: value === val ? C.amber : C.sub,
        }}>{lab}</button>
      );
    })}
  </div>
);

export interface HintProps { C: ColorPalette; icon?: ReactNode; children: ReactNode; tone?: string; }
export const Hint = ({ C, icon, children, tone }: HintProps) => {
  const co = tone ?? C.muted;
  return (
    <div style={{ marginTop: 14, display: 'flex', alignItems: 'flex-start', gap: 8, padding: '9px 12px', background: tone ? `${tone}14` : C.faint, border: `1px solid ${tone ? tone + '44' : C.border}`, borderRadius: 6, fontSize: vt(10.5, 'body'), color: tone ? co : C.muted, lineHeight: 1.55 }}>
      {icon && <span style={{ color: tone ?? C.amber, flexShrink: 0, marginTop: 1 }}>{icon}</span>}
      <span>{children}</span>
    </div>
  );
};

export interface ToggleProps { C: ColorPalette; on: boolean; onClick?: () => void; }
export const Toggle = ({ C, on, onClick }: ToggleProps) => (
  <span onClick={onClick} style={{ width: 34, height: 18, borderRadius: 9, background: on ? C.green : C.faint, border: `1px solid ${on ? C.green : C.border3}`, position: 'relative', display: 'inline-block', flexShrink: 0, cursor: onClick ? 'pointer' : 'default' }}>
    <span style={{ position: 'absolute', top: 1, left: on ? 17 : 1, width: 14, height: 14, borderRadius: '50%', background: '#fff', transition: 'all 150ms' }} />
  </span>
);

// ─── Visual Scale ─────────────────────────────────────────────────────────────
// Extracted from prototype source files. All values in px unless noted.
// Sources: tokens.jsx, shell.jsx, map-dashboard.jsx, zones.jsx, command.jsx,
//          alerts.jsx, movement.jsx, flagging.jsx, reports.jsx, admin.jsx

// Every font size used in the prototype, named by role.
export const FONT_SIZE = {
  sidebarMark: 8.5,  // shell.jsx:74 "Crowd Monitoring" sub-label; zones.jsx:526 wizard step-n label
  label:       9,    // tokens.jsx:141 KLabel; zones.jsx:99 table headers; command.jsx:264 zone-card code
  micro:       9.5,  // shell.jsx:132 user role; alerts.jsx elapsed timer; flagging.jsx flag timestamp
  xs:          10,   // tokens.jsx:186 tab count badge; tokens.jsx:125 Badge default; zones.jsx:926 field label
  sm:          10.5, // shell.jsx:150 topbar sub; flagging.jsx:94 privacy banner; reports.jsx template desc; zones.jsx:rule desc
  base:        11,   // shell.jsx:115 active-op name; zones.jsx:119 table status; alerts.jsx lifecycle label; tokens.jsx Btn.sm
  basePlus:    11.5, // zones.jsx:164 filter select; zones.jsx:527 wizard stepper step-name; flagging.jsx flag label
  body:        12,   // tokens.jsx:156 Btn.md; zones.jsx:404 activity text; movement.jsx congestion name; Tabs
  item:        12.5, // shell.jsx:91 nav items; zones.jsx:112 zone table name; admin.jsx user name; zones.jsx inputStyle
  sub:         13,   // tokens.jsx:157 Btn.lg; shell.jsx:73 logo text; zones.jsx:431 trigger type; flagging.jsx header title
  subheading:  14,   // shell.jsx:149 topbar title; admin.jsx:56 section titles; flagging.jsx zone name; alerts.jsx alert title
  stepTitle:   15,   // zones.jsx WizStep1–5 step titles (lines ~577 ~616 ~755 ~858 ~945)
  heading:     16,   // shell.jsx:281 zone panel name; command.jsx:222 KPI unit; admin.jsx scenario name; command.jsx event banner name
  title:       18,   // zones.jsx:35 screen title; map-dashboard.jsx:309 density "%" unit; command.jsx:op elapsed value; movement.jsx FlowStat
  display:     20,   // zones.jsx:237 zone detail header name; movement.jsx top stat values
  displaySm:   22,   // zones.jsx:317 anonymous composition count
  metric:      24,   // zones.jsx:295 live metrics KPI value
  metricLg:    28,   // alerts.jsx zone snapshot density at trigger point
  hero:        30,   // command.jsx:221 KPI card value (fontWeight 900)
  gauge:       42,   // map-dashboard.jsx:308 density gauge — largest font in the app
} as const;

// Named font weights matching prototype usage.
export const FONT_WEIGHT = {
  regular:  400,  // standard body and description text throughout
  medium:   500,  // map-dashboard.jsx:309 density "%" unit; tokens.jsx Btn.lg implicit
  semibold: 600,  // tokens.jsx:241 Btn; zones.jsx:112 zone table name; shell.jsx:91 nav items; zones.jsx:527 wizard step-name
  bold:     700,  // tokens.jsx:141 KLabel; all badges, table headers, primary numeric values throughout
  black:    900,  // command.jsx:221 KPI card value; alerts.jsx zone snapshot density; map-dashboard.jsx:308 gauge
} as const;

// Named letter-spacing values; values are CSS strings (em units).
export const LETTER_SPACING = {
  normal: 'normal',   // standard body text — no explicit tracking
  tight:  '0.01em',   // shell.jsx:149 topbar title — subtle positive tracking
  btn:    '0.02em',   // tokens.jsx:261 Btn letterSpacing
  wide:   '0.04em',   // tokens.jsx:200 Pill; alert status badges; risk-level text
  badge:  '0.05em',   // shell.jsx:172 topbar alert badge count; shell.jsx:132 user role
  label:  '0.06em',   // tokens.jsx:205 Badge; FlowStat labels; command.jsx:264 zone-card code; nav count badge
  field:  '0.08em',   // zones.jsx:926 field labels; zones.jsx:99 table headers; zones.jsx:526 wizard step-n; map-dashboard:209 layer label
  klabel: '0.1em',    // tokens.jsx:180 KLabel; command.jsx event banner "ACTIVE OPERATION" label
  brand:  '0.14em',   // shell.jsx:74 sidebar "Crowd Monitoring" sub-label — widest tracked text in app
} as const;

// Named line-height values; unitless ratios.
export const LINE_HEIGHT = {
  tight:   1,    // metric numbers, Pill text, button text
  chip:    1.1,  // alerts.jsx CountChip numeric values
  badge:   1.3,  // tokens.jsx:208 Badge lineHeight
  pill:    1.4,  // tokens.jsx:220 Pill lineHeight; admin.jsx roles can/cannot item list
  relaxed: 1.5,  // privacy notes, longer descriptions throughout all screens
} as const;

// Named border-radius values in px.
export const RADIUS = {
  xs:    2,    // tiny progress fills, minimal-rounding badges
  sm:    3,    // tokens.jsx:208 Badge; speed buttons; action-type badges in alerts / audit
  md:    4,    // zones.jsx:932 inputStyle; map attribution chip; filter selects
  card:  5,    // tokens.jsx:261 Btn; shell.jsx:91 nav items; ZoneCard; sparkline containers
  lg:    6,    // map-dashboard.jsx layer panel; zones.jsx zone-type preset cards; RuleRow
  panel: 8,    // tokens.jsx:226 Panel atom — standard card / modal border-radius
  tab:   9,    // tokens.jsx:288 Tabs count badge
  pill:  9999, // tokens.jsx:220 Pill; shell.jsx nav alert count badge — fully round
} as const;

// Spacing scale in px — covers every explicit padding / gap / margin in the prototype.
export const SPACE = {
  1:  1,
  2:  2,
  3:  3,
  4:  4,
  5:  5,
  6:  6,
  7:  7,
  8:  8,
  9:  9,
  10: 10,
  11: 11,
  12: 12,
  13: 13,
  14: 14,
  16: 16,
  18: 18,  // tokens.jsx Btn.lg padding-x; standard panel content padding
  20: 20,  // screen content area padding; topbar horizontal padding
  24: 24,  // right-panel detail area (alerts, flagging)
  28: 28,  // wizard stepper row area
  32: 32,  // wizard step content blocks
} as const;

// Fixed component dimensions in px.
export const SIZE = {
  // Layout chrome
  sidebar:     220,  // shell.jsx:57 — nav sidebar width
  topbar:       52,  // shell.jsx:145 — topbar height
  zonePanel:   340,  // map-dashboard.jsx:272 — right zone-status panel width
  alertColumn: 380,  // alerts.jsx / flagging.jsx — left alert/flag list panel width
  layerPanel:  230,  // map-dashboard.jsx:204 — top-left layer control panel width

  // Icons
  icon:    16,  // tokens.jsx:64 — default Ico render size
  iconSm:  12,  // most in-button icon sizes throughout
  iconNav: 15,  // shell.jsx:97 — sidebar nav item icons
  iconXs:  11,  // table action icons, small contextual indicators
  iconMd:  14,  // command.jsx:225 KPI trend arrows; alerts.jsx lifecycle check icons
  dot:      6,  // flagging.jsx list status indicators

  // Horizontal density / capacity bars
  barXs:  3,  // zones.jsx:318 — composition mini bars in zone overview
  barSm:  4,  // zones.jsx:127 — zone table density bars; zone list items
  barMd:  5,  // command.jsx:276 ZoneCard density bar; movement.jsx congestion bar
  barLg:  8,  // map-dashboard.jsx:325 — zone panel capacity bar (largest bar in app)
  capacityBar: 4,  // flagging.jsx session cap bar height

  // Alert severity left-border indicator
  severityBar: 3,  // alerts.jsx — alert list item left-border width

  // Avatar / icon containers
  avatarSm: 24,  // command.jsx operations team member initials circles
  avatarMd: 30,  // shell.jsx:125 sidebar user card; topbar user avatar
  avatarLg: 44,  // command.jsx:225 event banner shield-icon background container

  // Command dashboard specifics
  iconLg:         20,  // command.jsx:26 — shield icon in event banner
  teamIcon:       36,  // command.jsx:157 — operations team icon container
  kpiSparkline:   30,  // command.jsx:244 — KPI card sparkline SVG height
  scrollListMax: 380,  // command.jsx:101 — alert/flag list max-height in command panel
  teamAlertCount: 60,  // command.jsx:175 — team alert count column min-width

  // Movement screen — two-column layout (no prototype reference — follows design system)
  movementPanel: 440,  // left analytics column width; right map fills remaining flex space

  // Alert screen specifics
  iconXl:        24,  // alerts.jsx detail panel action icons; empty state indicators
  iconXxl:       36,  // alerts.jsx empty state Bell icon — largest icon in screen
  stepCircle:    30,  // alerts.jsx lifecycle stepper — step circle diameter
  actionLogTime: 90,  // alerts.jsx action log — timestamp column fixed width
  actionLogWho:  70,  // alerts.jsx action log — type badge column fixed width
  textareaMin:   72,  // alerts.jsx resolution note textarea — minimum height (≈3 rows)

  // Flagging screen specifics
  flagModal:    480,  // A6 create flag modal width
  flagChipMin:  80,  // flagging.jsx movement action chip min-width
  flagTimeCol:  50,  // flagging.jsx movement timestamp column min-width
  flagDropdown: 220,  // A6 add-zone dropdown min-width
  flagZoneList: 180,  // A6 create modal zone checklist max-height

  // Operation creation modal — FOP-S4
  opModal: 560,  // operation creation modal width

  // Reporting screen specifics
  reportIconTile: 34,  // reports.jsx template card icon tile
  reportModal:   520,  // reports.jsx generate modal width
  reportDescMin: 30,   // reports.jsx template card description minimum height
  reportTimeCol: 65,   // reports.jsx audit trail timestamp column
  reportActorCol: 100, // reports.jsx audit trail actor column
  reportActionCol: 160, // reports.jsx audit trail action badge column
} as const;
